import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus } from "lucide-react";
import { ui } from "../../../components/ui";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";
import { CRM_ONBOARDING_LIST_PATH } from "../../crm/routes";
import {
  fetchContacts,
  fetchCustomers,
  fetchIndustryTypes,
  getContact,
  getCustomer,
} from "../../../services/leadManagementService";
import {
  LEAD_SOURCES,
  LEAD_STATUSES,
  LOCKED_STATUSES,
  MAX_LENGTH,
  PROJECT_TYPES,
  RELATIONSHIP_LEVELS,
  RELATIONSHIP_LEVEL_LABELS,
  leadKey,
  toOptions,
  valueLabel,
} from "../leadForm";

/* ----------------------------------------------------------- field parts */

export const inputClass = cn(ui.input, "h-10 w-full disabled:bg-gray-50 disabled:cursor-not-allowed");
const errorBorder = "border-rose-400";
const grid = "form-grid grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

/** A titled part of a card, set off from the part above by a rule. */
export function FormSection({ title, first = false, children }) {
  return (
    <section className={cn(!first && "border-t border-(--color-text-border) pt-5")}>
      <h3 className="mb-4 text-sm font-bold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

export function Field({ label, htmlFor, required, error, className, children }) {
  return (
    <div className={cn("min-w-0", className)}>
      <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold text-foreground">
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>
      {children}
      {error ? <p className="mt-1 text-[11px] font-semibold text-rose-600">{error}</p> : null}
    </div>
  );
}

/** A read-only value in the same box shape as an input. */
export function ReadOnlyValue({ id, value }) {
  return (
    <p
      id={id}
      className="flex h-10 items-center truncate rounded-xl border border-(--color-text-border) bg-gray-50 px-3 text-sm font-semibold per-file-hint"
    >
      {value || "—"}
    </p>
  );
}

/** children render above the options, just under the menu's search, e.g. an action. */
export function SelectInput({ id, value, options, placeholder, disabled, error, onChange, onOpen, children, className }) {
  return (
    <Select
      value={value || undefined}
      disabled={disabled}
      onValueChange={onChange}
      onOpenChange={(open) => open && onOpen?.()}
    >
      <SelectTrigger
        id={id}
        className={cn("h-10 w-full rounded-xl border border-(--color-text-border) bg-white text-sm", error && errorBorder, className)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-60 overflow-y-auto">
        {children}
        {options.map((option) => (
          <SelectItem key={option.value} value={String(option.value)}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Yes / No as a switch: on is Yes, off is No. */
function YesNo({ id, value, disabled, onChange }) {
  const { t } = useTranslation();
  const on = value === true;
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className="flex h-10 cursor-pointer items-center gap-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className={cn("relative h-6 w-11 shrink-0 rounded-full transition-colors", on ? "bg-primary" : "bg-gray-300")}>
        <span className={cn("absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform", on && "translate-x-5")} />
      </span>
      <span className={on ? "text-foreground" : "per-file-hint"}>{t(on ? "common.yes" : "common.no")}</span>
    </button>
  );
}

/** A box of checkboxes with responsive columns for concise display. */
function ScrollableCheckboxBox({ id, options, selected, disabled, error, onToggle }) {
  const { t } = useTranslation();
  return (
    <div
      id={id}
      className={cn(
        "max-h-[140px] w-full overflow-y-auto rounded-xl border border-(--color-text-border) bg-white p-2 [scrollbar-width:thin]",
        disabled && "pointer-events-none opacity-50 bg-gray-50",
        error && errorBorder
      )}
    >
      {options && options.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
          {options.map((option) => (
            <label
              key={option}
              className="flex h-[34px] cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-foreground transition hover:bg-workspace/60"
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={selected.includes(option)}
                onChange={() => onToggle(option)}
                className="h-4 w-4 shrink-0 cursor-pointer accent-primary"
              />
              <span className="min-w-0 truncate select-none text-xs sm:text-sm font-medium">{option}</span>
            </label>
          ))}
        </div>
      ) : (
        <div className="flex h-10 items-center justify-center text-xs per-file-hint">
          {t("leadManagement.placeholdersSelect")}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ form */

/**
 * The lead's own fields, grouped as the PRD lays them out.
 *
 * mode "create" adds the opening status; mode "edit" leaves the status to the
 * header and adds the schedule and requirement fields of Lead Information.
 */
export function LeadForm({ mode = "create", form, errors = {}, lookups, lead, disabled = false, onChange }) {
  const { t } = useTranslation();
  const isCreate = mode === "create";
  const [customers, setCustomers] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [verticals, setVerticals] = useState([]);

  // A new lead starts in INR.
  useEffect(() => {
    const inr = lookups.currencies.find((row) => row.label === "INR");
    if (isCreate && inr && !form.projectValueMetric) onChange("projectValueMetric", String(inr.id));
  }, [lookups.currencies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Verticals are read the first time the dropdown is opened.
  const loadVerticals = () => {
    if (!verticals.length) fetchIndustryTypes().then(setVerticals);
  };

  // A saved lead shows its customer and contact before either list is opened.
  useEffect(() => {
    if (lead?.customerId) getCustomer(lead.customerId).then((row) => setCustomers([row]));
    if (lead?.pointOfContactId) getContact(lead.pointOfContactId).then((row) => setContacts([row]));
  }, [lead]);

  // Customers are read the first time the dropdown is opened.
  const loadCustomers = () => {
    if (customersLoaded) return;
    setCustomersLoaded(true);
    fetchCustomers().then(setCustomers);
  };

  const loadContacts = () => fetchContacts(form.customerId).then(setContacts);

  const pickCustomer = (value) => {
    setContacts([]);
    onChange("customerId", value);
  };

  // The chosen contact's own email and phone fill the read-only fields.
  const pickContact = (value) => {
    onChange("pointOfContactId", value);
    getContact(value).then((contact) => {
      onChange("contactEmail", contact.email);
      onChange("contactPhone", contact.phone);
    });
  };

  const field = (key, control, props = {}) => (
    <Field key={key} label={t(leadKey("fields", key))} htmlFor={`lead-${key}`} error={errors[key]} {...props}>
      {control}
    </Field>
  );

  const text = (key, props = {}) => (
    <input
      id={`lead-${key}`}
      value={form[key]}
      disabled={disabled}
      maxLength={MAX_LENGTH[key]}
      onChange={(event) => onChange(key, event.target.value)}
      className={cn(inputClass, errors[key] && errorBorder)}
      {...props}
    />
  );

  const select = (key, options, placeholderKey, extraDisabled = false) => (
    <SelectInput
      id={`lead-${key}`}
      value={form[key]}
      options={options}
      placeholder={t(placeholderKey)}
      disabled={disabled || extraDisabled}
      error={errors[key]}
      onChange={(value) => onChange(key, value)}
    />
  );

  const yesNo = (key) => (
    <YesNo id={`lead-${key}`} value={form[key]} disabled={disabled} onChange={(value) => onChange(key, value)} />
  );

  const toggleSystem = (name) =>
    onChange(
      "systemsRequired",
      form.systemsRequired.includes(name)
        ? form.systemsRequired.filter((entry) => entry !== name)
        : [...form.systemsRequired, name]
    );

  const customerOptions = customers.map((row) => ({ value: row.id, label: row.name }));
  const contactOptions = contacts.map((row) => ({ value: row.id, label: row.name }));
  // Until the list is opened, the saved vertical is the only option.
  const verticalOptions = (verticals.length ? verticals : [form.leadVertical].filter(Boolean)).map((name) => ({
    value: name,
    label: name,
  }));
  const currencyOptions = lookups.currencies.map((row) => ({ value: row.id, label: row.label }));
  const levelOptions = RELATIONSHIP_LEVELS.map((level) => ({
    value: level,
    label: t(`leadManagement.relationshipLevels.${level}`, RELATIONSHIP_LEVEL_LABELS[level] || String(level)),
  }));
  // Lost and Fully Invoiced are reached through their own flows, never at creation.
  const openingStatuses = LEAD_STATUSES.filter((status) => !LOCKED_STATUSES.includes(status));

  return (
    <div className="space-y-5">
      <FormSection title={t("leadManagement.sectionsSource")} first>
        <div className={grid}>
          {isCreate
            ? field("leadStatus", select("leadStatus", toOptions(openingStatuses), "leadManagement.placeholdersStatus"), { required: true })
            : null}
          {field("leadSource", select("leadSource", toOptions(LEAD_SOURCES), "leadManagement.placeholdersSelect"), { required: true })}
          {field(
            "leadVertical",
            <SelectInput
              id="lead-leadVertical"
              value={form.leadVertical}
              options={verticalOptions}
              placeholder={t("leadManagement.placeholdersSelect")}
              disabled={disabled}
              error={errors.leadVertical}
              onOpen={loadVerticals}
              onChange={(value) => onChange("leadVertical", value)}
            />,
            { required: true }
          )}
        </div>
      </FormSection>

      <FormSection title={t("leadManagement.sectionsCustomer")}>
        <div className={grid}>
          {field(
            "customerId",
            <SelectInput
              id="lead-customerId"
              value={form.customerId}
              options={customerOptions}
              placeholder={t("leadManagement.placeholdersCustomer")}
              disabled={disabled}
              error={errors.customerId}
              onOpen={loadCustomers}
              onChange={pickCustomer}
            >
              <Link
                to={CRM_ONBOARDING_LIST_PATH}
                className="sticky top-0 z-10 mb-1 flex items-center gap-2 rounded-sm border-b border-(--color-text-border) bg-popover px-2 py-2 text-sm font-semibold text-primary hover:bg-accent hover:text-accent-foreground"
              >
                <Plus className="h-4 w-4" />
                {t("formActions.addCustomer")}
              </Link>
            </SelectInput>,
            { required: true }
          )}
          {field(
            "pointOfContactId",
            <SelectInput
              id="lead-pointOfContactId"
              value={form.pointOfContactId}
              options={contactOptions}
              placeholder={t(form.customerId ? "leadManagement.placeholdersContact" : "leadManagement.placeholdersCustomerFirst")}
              disabled={disabled || !form.customerId}
              error={errors.pointOfContactId}
              onOpen={loadContacts}
              onChange={pickContact}
            />
          )}
          {field("contactEmail", <ReadOnlyValue id="lead-contactEmail" value={form.contactEmail} />)}
          {field("contactPhone", <ReadOnlyValue id="lead-contactPhone" value={form.contactPhone} />)}
          <div className="col-span-full grid grid-cols-2 gap-4 xl:contents">
            {field("isKeyAccount", yesNo("isKeyAccount"))}
            {field("isExistingCustomer", yesNo("isExistingCustomer"))}
          </div>
          {field("customerRelationshipLevel", select("customerRelationshipLevel", levelOptions, "leadManagement.placeholdersSelect"))}
        </div>
      </FormSection>

      <FormSection title={t("leadManagement.sectionsProject")}>
        <div className={grid}>
          {field("projectName", text("projectName"), { required: true })}
          {field("projectLocation", text("projectLocation"))}
          {field("projectType", select("projectType", toOptions(PROJECT_TYPES),"leadManagement.placeholdersSelect"), { required: true })}
          {field(
            "projectValueAmount",
            <div className="flex gap-2">
              <div className="w-28 shrink-0">
                <SelectInput
                  id="lead-projectValueMetric"
                  value={form.projectValueMetric}
                  options={currencyOptions}
                  placeholder={t("leadManagement.placeholdersCurrency")}
                  disabled={disabled}
                  onChange={(value) => onChange("projectValueMetric", value)}
                />
              </div>
              {text("projectValueAmount", { type: "number", min: 0, step: "0.01", inputMode: "decimal" })}
            </div>,
            { required: true }
          )}
          {field(
            "systemsRequired",
            <ScrollableCheckboxBox
              id="lead-systemsRequired"
              options={lookups.systems}
              selected={form.systemsRequired}
              disabled={disabled}
              error={errors.systemsRequired}
              onToggle={toggleSystem}
            />,
            { required: true, className: "sm:col-span-2 lg:col-span-3 xl:col-span-4" }
          )}
        </div>
      </FormSection>

      {!isCreate ? (
        <FormSection title={t("leadManagement.sectionsSchedule")}>
          <div className={grid}>
            {field("boqReceivedDate", text("boqReceivedDate", { type: "date" }))}
            {field("offerDueDate", text("offerDueDate", { type: "date" }))}
            {field("orderFinalizationByDate", text("orderFinalizationByDate", { type: "date" }))}
            {field("customerPaymentCredentials", text("customerPaymentCredentials"))}
            <div className="col-span-full grid grid-cols-2 gap-4 sm:grid-cols-4">
              {["siteVisitRequired", "surveyRequired", "boqGenerated", "isDesignAndBuild"].map((key) => field(key, yesNo(key)))}
            </div>
          </div>
        </FormSection>
      ) : null}
    </div>
  );
}

/** The lost reason and note recorded when a lead was closed as Lost. */
export function LostDetails({ lead }) {
  const { t } = useTranslation();
  if (!lead.lostReason) return null;
  return (
    <FormSection title={t("leadManagement.sectionsLost")}>
      <div className={grid}>
        <Field label={t("leadManagement.fieldsLostReason")}>
          <ReadOnlyValue value={valueLabel(lead.lostReason)} />
        </Field>
        <Field label={t("leadManagement.fieldsLostNote")} className="sm:col-span-2">
          <p className="min-h-10 whitespace-pre-wrap rounded-xl border border-(--color-text-border) bg-gray-50 px-3 py-2.5 text-sm per-file-hint">
            {lead.lostNote}
          </p>
        </Field>
      </div>
    </FormSection>
  );
}

export default LeadForm;
